'use client';

/**
 * MFA verification — ui-ux-pro-max gold/cream shell
 */
import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Form, Input, Button } from 'antd';
import {
  SafetyOutlined,
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/domains/auth/context/auth-context';
import { useTheme, ThemeToggle } from '@/shared/theme';
import '@/shared/theme/styles/login.css';
import styles from './AuthLayout.module.css';

const MFAVerificationPage: React.FC = () => {
  const [form] = Form.useForm();
  const router = useRouter();
  const { verifyMFA, requiresMFA, mfaUserId, mfaMethods } = useAuth();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);

  const userId = mfaUserId;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!requiresMFA && !userId) {
      router.push('/auth/login');
    }
  }, [requiresMFA, userId, router]);

  useEffect(() => {
    if (!error) return;
    const t = window.setTimeout(() => errorRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [error]);

  const onFinish = async (values: { token: string }) => {
    if (!userId) {
      setError('Missing user ID for MFA verification');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await verifyMFA({
        user_id: userId,
        mfa_token: values.token,
      });

      if (result.success) {
        router.push('/dashboard');
      } else {
        setError(result.error || 'MFA verification failed');
      }
    } catch (err) {
      console.error('MFA verification error:', err);
      setError(err instanceof Error ? err.message : 'MFA verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    router.push('/auth/login');
  };

  return (
    <div
      className={`login-page ${theme.isDark ? 'dark' : 'light'} ${styles.page}`}
      data-theme={theme.isDark ? 'dark' : 'light'}
    >
      <nav className="login-navbar" aria-label="Primary">
        <div className="login-navbar-container">
          <Button
            type="text"
            icon={<ArrowLeftOutlined aria-hidden />}
            onClick={handleBackToLogin}
            className={styles.navBack}
          >
            Back to sign in
          </Button>
          <ThemeToggle size={isMobile ? 'small' : 'medium'} />
        </div>
      </nav>

      <div className={styles.body}>
        <div className={styles.formContainer}>
          <div className={styles.card}>
            <header className={styles.formHeaderCenter}>
              <div className={styles.heroIcon} aria-hidden>
                <SafetyOutlined />
              </div>
              <h1 className={styles.formTitle}>Two-factor authentication</h1>
              <p className={styles.formSubtitle}>
                Enter the verification code from your authenticator app to complete sign-in
              </p>
            </header>

            {error && (
              <div
                ref={errorRef}
                className={styles.errorAlert}
                role="alert"
                tabIndex={-1}
                id="mfa-verify-error"
              >
                <ExclamationCircleOutlined className={styles.errorIcon} aria-hidden />
                <div className={styles.errorContent}>
                  <div className={styles.errorTitle}>Verification failed</div>
                  <div className={styles.errorText}>{error}</div>
                </div>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => setError(null)}
                  className={styles.dismissBtn}
                  aria-label="Dismiss error"
                />
              </div>
            )}

            {mfaMethods && mfaMethods.length > 0 && (
              <div className={styles.methodsChip} role="status">
                Available methods: {mfaMethods.join(', ')}
              </div>
            )}

            <Form
              form={form}
              name="mfa"
              onFinish={onFinish}
              layout="vertical"
              requiredMark={false}
              className={styles.formFields}
              disabled={loading}
              aria-busy={loading}
            >
              <Form.Item
                name="token"
                htmlFor="mfa-token"
                label={<span className={styles.fieldLabel}>Verification code</span>}
                rules={[
                  { required: true, message: 'Enter your verification code' },
                  { len: 6, message: 'Code must be 6 digits' },
                  { pattern: /^\d{6}$/, message: 'Code must contain only digits' },
                ]}
                validateTrigger={['onBlur', 'onSubmit']}
              >
                <Input
                  id="mfa-token"
                  name="token"
                  placeholder="000000"
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className={styles.mfaInput}
                  aria-required="true"
                  aria-describedby={error ? 'mfa-verify-error' : undefined}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                icon={!loading ? <ArrowRightOutlined /> : undefined}
                iconPlacement="end"
                className={styles.submitBtn}
              >
                {loading ? 'Verifying…' : 'Verify code'}
              </Button>
            </Form>

            <div className={styles.footerRow} style={{ marginTop: 16 }}>
              Can&apos;t access your authenticator?{' '}
              <button
                type="button"
                className={styles.linkBtn}
                style={{ background: 'none', border: 'none', font: 'inherit' }}
                onClick={() => {
                  /* backup codes flow */
                }}
              >
                Use backup code
              </button>
            </div>

            <div className={styles.securityBadge}>
              <SafetyOutlined className={styles.securityBadgeIcon} aria-hidden />
              Enhanced security with multi-factor authentication
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAVerificationPage;
